import { databases, DATABASE_ID, COLLECTION_ID_SUPPLIERS, COLLECTION_ID_PURCHASE_ORDERS } from '../config/appwrite';
import { ID, Query } from 'appwrite';
import { normalizeSupplierNameForMatching } from '../utils/supplierNameNormalize';

/**
 * Suppliers Service
 * Handles all database operations for Suppliers
 * Collection Name: "Suppliers 22"
 * Collection ID: "Suppliers22" (Appwrite doesn't allow spaces in collection IDs)
 */

// Helper function to parse currency string to number
const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;
  // Handle both string and number types
  if (typeof currencyString === 'number') return currencyString;
  const cleaned = currencyString.toString().replace(/TZS\s?/gi, '').replace(/,/g, '').trim();
  return parseFloat(cleaned) || 0;
};

/**
 * Same PO amount basis as Dashboard / Purchase Orders (poAmount if set, else totalAmount).
 * Supplier totals were previously summing only totalAmount, which did not match the dashboard.
 */
const getPoAmountForAggregation = (po) => {
  const rawTotal = typeof po.totalAmount === 'number'
    ? po.totalAmount
    : parseCurrency(po.totalAmount || 0);
  const rawPo = typeof po.poAmount === 'number'
    ? po.poAmount
    : parseCurrency(po.poAmount || 0);
  return rawPo > 0 ? rawPo : rawTotal;
};

/** Match POs to a supplier name using trim + case-insensitive (must match Dashboard grouping). */
const computeStatsFromPoList = (rawPos, supplierName) => {
  if (!supplierName || supplierName.trim() === '') {
    return { numberOfPO: 0, totalPOAmount: 0 };
  }
  const normalized = normalizeSupplierNameForMatching(supplierName);
  const pos = rawPos.filter(
    (po) => normalizeSupplierNameForMatching(po.supplierName || '') === normalized
  );
  const numberOfPO = pos.length;
  const totalPOAmount = pos.reduce((sum, po) => sum + getPoAmountForAggregation(po), 0);
  return { numberOfPO, totalPOAmount };
};

const fetchPurchaseOrdersRaw = async () => {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_PURCHASE_ORDERS,
    [Query.limit(1000)]
  );
  return response.documents || [];
};

/**
 * PO stats for one supplier. Always uses normalized name matching (fixes under-count when
 * exact Appwrite match returned some rows but missed POs with different casing).
 */
const getSupplierPOStats = async (supplierName) => {
  try {
    const rawPos = await fetchPurchaseOrdersRaw();
    return computeStatsFromPoList(rawPos, supplierName);
  } catch (error) {
    console.error(`Error fetching PO stats for ${supplierName}:`, error);
    return { numberOfPO: 0, totalPOAmount: 0 };
  }
};

// Get all suppliers with calculated PO statistics and update database
export const getAllSuppliers = async () => {
  try {
    const [suppliersResponse, rawPos] = await Promise.all([
      databases.listDocuments(
        DATABASE_ID,
        COLLECTION_ID_SUPPLIERS,
        [
          Query.orderDesc('$createdAt'),
          Query.limit(1000)
        ]
      ),
      fetchPurchaseOrdersRaw()
    ]);

    const suppliersWithStats = await Promise.all(
      suppliersResponse.documents.map(async (doc) => {
        const supplier = formatSupplier(doc);
        const stats = computeStatsFromPoList(rawPos, supplier.name);

        const currentNumberOfPO = doc.numberOfPO || 0;
        const currentTotalPOAmount = doc.totalPOAmount || 0;

        if (currentNumberOfPO !== stats.numberOfPO || currentTotalPOAmount !== stats.totalPOAmount) {
          try {
            await databases.updateDocument(
              DATABASE_ID,
              COLLECTION_ID_SUPPLIERS,
              supplier.id,
              {
                numberOfPO: stats.numberOfPO,
                totalPOAmount: stats.totalPOAmount
              }
            );
          } catch (updateError) {
            console.error(`Error updating PO stats for supplier ${supplier.name}:`, updateError);
          }
        }

        return {
          ...supplier,
          numberOfPO: stats.numberOfPO,
          totalPOAmount: stats.totalPOAmount
        };
      })
    );

    return suppliersWithStats;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

// Get a single supplier by ID
export const getSupplierById = async (id) => {
  try {
    const response = await databases.getDocument(
      DATABASE_ID,
      COLLECTION_ID_SUPPLIERS,
      id
    );
    return formatSupplier(response);
  } catch (error) {
    console.error('Error fetching supplier:', error);
    throw error;
  }
};

// Create a new supplier
export const createSupplier = async (supplierData) => {
  try {
    // Calculate initial PO stats for new supplier
    const stats = await getSupplierPOStats(supplierData.name || '');
    
    const documentData = {
      name: supplierData.name || '',
      contact: supplierData.contact || '',
      phone: supplierData.phone || '',
      email: supplierData.email || '',
      address: supplierData.address || '',
      category: supplierData.category || '',
      numberOfPO: stats.numberOfPO,
      totalPOAmount: stats.totalPOAmount
    };

    const response = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_SUPPLIERS,
      ID.unique(),
      documentData
    );
    return formatSupplier(response);
  } catch (error) {
    console.error('Error creating supplier:', error);
    throw error;
  }
};

// Update a supplier
export const updateSupplier = async (id, supplierData) => {
  try {
    const documentData = {};
    
    // Get current supplier to check if name changed
    const currentSupplier = await getSupplierById(id);
    const newName = supplierData.name !== undefined ? supplierData.name : currentSupplier.name;
    
    if (supplierData.name !== undefined) documentData.name = supplierData.name;
    if (supplierData.contact !== undefined) documentData.contact = supplierData.contact;
    if (supplierData.phone !== undefined) documentData.phone = supplierData.phone;
    if (supplierData.email !== undefined) documentData.email = supplierData.email;
    if (supplierData.address !== undefined) documentData.address = supplierData.address;
    if (supplierData.category !== undefined) documentData.category = supplierData.category;
    
    // Always recalculate PO stats from Purchase Orders table
    const stats = await getSupplierPOStats(newName);
    documentData.numberOfPO = stats.numberOfPO;
    documentData.totalPOAmount = stats.totalPOAmount;

    const response = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_SUPPLIERS,
      id,
      documentData
    );
    return formatSupplier(response);
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
};

// Delete a supplier
export const deleteSupplier = async (id) => {
  try {
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTION_ID_SUPPLIERS,
      id
    );
    return { success: true };
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};

// Refresh PO statistics for all suppliers in the database
export const refreshAllSupplierPOStats = async () => {
  try {
    const suppliers = await getAllSuppliers();
    return suppliers;
  } catch (error) {
    console.error('Error refreshing supplier PO stats:', error);
    throw error;
  }
};

// Format supplier from Appwrite document format to app format
const formatSupplier = (doc) => {
  return {
    id: doc.$id,
    name: doc.name || '',
    contact: doc.contact || '',
    phone: doc.phone || '',
    email: doc.email || '',
    address: doc.address || '',
    category: doc.category || '',
    numberOfPO: doc.numberOfPO || 0,
    totalPOAmount: doc.totalPOAmount || 0
  };
};
